import { Enrollment } from '@spike/membership-model';
import { PaymentMethodIds } from '@spike/payments-model';
import { useCallback, useState } from 'react';

export interface CheckoutInfo {
    orderId: number | null;
    enrollmentId: number | null;
    paymentMethods: Array<PaymentMethodIds> | undefined;
}

export interface EnrollmentCheckoutProps {
    checkoutInfo: CheckoutInfo;
    onClose: () => void;
    onPaid?: () => void;
}

export interface UseEnrollmentCheckoutReturn {
    checkoutInfo: CheckoutInfo;
    openCheckout: (enrollment: Enrollment) => void;
    closeCheckout: () => void;
    handlePaymentSuccess: (onSuccess?: () => void) => void;
    isCheckoutOpen: boolean;
}

export const useEnrollmentCheckout = (): UseEnrollmentCheckoutReturn => {
    const [checkoutInfo, setCheckoutInfo] = useState<CheckoutInfo>({
        orderId: null,
        enrollmentId: null,
        paymentMethods: undefined
    });

    const openCheckout = useCallback((enrollment: Enrollment) => {
        if (!enrollment) return;

        const paymentMethods =
            enrollment.membership.enrollmentType === 'package'
                ? [
                      PaymentMethodIds.Cash,
                      PaymentMethodIds.CreditCard,
                      PaymentMethodIds.POS,
                      PaymentMethodIds.PayByLink,
                      PaymentMethodIds.Other
                  ]
                : enrollment.membership.enrollmentType === 'subscription'
                ? [PaymentMethodIds.CreditCard, PaymentMethodIds.POS, PaymentMethodIds.PayByLink]
                : undefined;

        setCheckoutInfo({
            orderId: enrollment.orderId,
            enrollmentId: enrollment.id,
            paymentMethods
        });
    }, []);

    const closeCheckout = useCallback(() => {
        setCheckoutInfo({
            orderId: null,
            enrollmentId: null,
            paymentMethods: undefined
        });
    }, []);

    const handlePaymentSuccess = useCallback(
        (onSuccess?: () => void) => {
            closeCheckout();

            if (onSuccess) {
                onSuccess();
            }
        },
        [closeCheckout]
    );

    const isCheckoutOpen = Boolean(checkoutInfo.orderId);

    return {
        checkoutInfo,
        openCheckout,
        closeCheckout,
        handlePaymentSuccess,
        isCheckoutOpen
    };
};

export default useEnrollmentCheckout;
